import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Lock, Loader2 } from "lucide-react";
import { Brand, LanguageToggle } from "../components/Brand";
import { firstAvailablePath } from "../components/ProtectedRoute";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const DEMO_ACCOUNTS = [
  { label: "Super Admin", email: "superadmin@filepro.com", password: "SuperAdmin123!" },
  { label: "Clinic Admin", email: "admin@cliniquesanteplus.com", password: "Admin123!" },
  { label: "Agent (Réception)", email: "agent@cliniquesanteplus.com", password: "Agent123!" },
];

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || t("invalid_credentials"));
      return;
    }
    const u = res.user;
    if (u.must_change_password) {
      navigate("/force-password");
      return;
    }
    const from = location.state?.from?.pathname;
    if (from) {
      navigate(from);
      return;
    }
    navigate(firstAvailablePath(u));
  };

  const fillDemo = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col p-6 sm:p-10">
        <div className="flex items-center justify-between">
          <Brand />
          <LanguageToggle />
        </div>

        <div className="flex-1 grid place-items-center py-10">
          <div className="w-full max-w-md fade-in-up">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              {t("login")}
            </h1>
            <p className="mt-2 text-slate-600">{t("tagline")}</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">{t("email")}</span>
                <div className="mt-1 relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
                    placeholder="admin@cliniquesanteplus.com"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">{t("password")}</span>
                <div className="mt-1 relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
                    placeholder="••••••••"
                  />
                </div>
              </label>

              {error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-60 transition"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t("sign_in")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">{t("demo_accounts")}</div>
              <div className="space-y-2">
                {DEMO_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    onClick={() => fillDemo(a)}
                    data-testid={`demo-fill-${a.label.replace(/\s+/g, '-').toLowerCase()}`}
                    className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-white transition flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">{a.label}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </div>
                    <span className="text-xs text-blue-700 font-medium">{t("use_account")}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 text-sm text-slate-500 flex items-center justify-between">
              <Link to="/" className="hover:text-slate-900" data-testid="back-home-link">← {t("back_to_home")}</Link>
              <Link to="/forgot-password" className="text-blue-700 hover:text-blue-900" data-testid="forgot-password-link">{t("forgot_password")}</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1618347991375-11c52cf8a38f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
          alt="Healthcare abstract"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/70 via-blue-700/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/30 text-xs font-medium">
            {t("demo_banner")}
          </div>
          <h2 className="mt-4 font-heading text-4xl font-bold leading-tight max-w-lg">
            {t("tagline")}
          </h2>
        </div>
      </div>
    </div>
  );
}
